/**
 * Composable for locale-aware date, time, and number formatting.
 *
 * Integrates with vue-i18n to automatically use the current locale.
 * Falls back to Dutch (nl-NL) if vue-i18n is not available.
 */
import { computed, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  formatDateShort as formatDateShortUtil,
  formatDateLong as formatDateLongUtil,
  formatTime as formatTimeUtil,
  formatDateTime as formatDateTimeUtil,
  formatRelativeTime as formatRelativeTimeUtil,
  formatAppointmentDateTime as formatAppointmentDateTimeUtil,
  formatDateCustom as formatDateCustomUtil,
  formatTimeRange as formatTimeRangeUtil,
  formatDateWithWeekday as formatDateWithWeekdayUtil,
  formatDateShortWithWeekday as formatDateShortWithWeekdayUtil,
  formatDateDigits as formatDateDigitsUtil,
  formatDateNumeric,
  formatNumber as formatNumberUtil,
  formatCurrency as formatCurrencyUtil,
  formatPercent as formatPercentUtil,
  formatFileSize as formatFileSizeUtil,
  truncateText,
  toIntlLocale,
  type DateInput,
  type SupportedLocale,
} from '@/utils/formatters'
import {
  getFullName as getFullNameUtil,
  getInitials as getInitialsUtil,
  type UserLike,
} from '@/utils/userFormatting'
import type { ContractStatus } from '@/api/generated-contracts'

export interface UseFormattersReturn {
  /** The current Intl locale (e.g., 'nl-NL' or 'en-US') */
  intlLocale: ComputedRef<SupportedLocale>

  // Duration formatting
  /** Format duration in minutes to human-readable string: "1 uur 30 minuten" (nl) / "1 hour 30 minutes" (en) */
  formatDuration: (minutes: number) => string

  // Date formatting
  /** Format date as short string: "28 feb. 2026" (nl) / "Feb 28, 2026" (en) */
  formatDateShort: (date: DateInput) => string
  /** Format date as long string: "28 februari 2026" (nl) / "February 28, 2026" (en) */
  formatDateLong: (date: DateInput) => string
  /** Format time: "14:30" (nl) / "2:30 PM" (en) */
  formatTime: (date: DateInput) => string
  /** Format date with time: "28 februari 2026 om 14:30" */
  formatDateTime: (date: DateInput) => string
  /** Format relative time: "2 uur geleden" (nl) / "2 hours ago" (en) */
  formatRelativeTime: (date: DateInput) => string
  /** Format appointment datetime with weekday: "ma 28 feb. 14:30" */
  formatAppointmentDateTime: (date: DateInput) => string
  /** Format date with custom Intl.DateTimeFormat options */
  formatDateCustom: (date: DateInput, options: Intl.DateTimeFormatOptions) => string
  /** Format time range: "14:30 - 15:30" (nl) / "2:30 PM - 3:30 PM" (en) */
  formatTimeRange: (startDate: DateInput, endDate: DateInput) => string
  /** Format date with weekday: "maandag 28 februari" (nl) / "Monday, February 28" (en) */
  formatDateWithWeekday: (date: DateInput) => string
  /** Format date in short weekday format: "ma 28 feb." (nl) / "Mon Feb 28" (en) */
  formatDateShortWithWeekday: (date: DateInput) => string
  /** Format date as two-digit day and month: "28-02-2026" (nl) / "02/28/2026" (en) */
  formatDateDigits: (date: DateInput) => string
  /** Format date as DD-MM-YYYY in every locale: "28-02-2026" */
  formatDateNumeric: (date: DateInput) => string

  // Number formatting
  /** Format number with locale separators: "1.234,56" (nl) / "1,234.56" (en) */
  formatNumber: (value: number | undefined | null, options?: Intl.NumberFormatOptions) => string
  /** Format currency: "€ 1.234,56" (nl) / "€1,234.56" (en) */
  formatCurrency: (amount: number | undefined | null, currency?: string) => string
  /**
   * The same, but an amount nobody has stated reads as an em dash rather than a hyphen.
   *
   * For a figure whose absence is itself the answer — what an insurer granted on a batch it has
   * not answered, a price a type does not set. `formatCurrency` already answers `-` there, and
   * every caller that wanted the wider dash was writing its own null branch to get one.
   */
  formatCurrencyOrDash: (amount: number | undefined | null, currency?: string) => string
  /** Format percentage: "12,5%" (nl) / "12.5%" (en) */
  formatPercent: (
    value: number | undefined | null,
    minimumFractionDigits?: number,
    maximumFractionDigits?: number
  ) => string
  /** Format a byte count as a file size: "332,5 KB" (nl) / "332.5 KB" (en) */
  formatFileSize: (bytes: number | undefined | null) => string

  // Boolean formatting
  /** Format boolean as "Ja"/"Nee" (nl) or "Yes"/"No" (en) */
  formatBoolean: (value: boolean | null | undefined) => string

  // Text formatting
  /** Truncate text to a maximum length, adding ellipsis if truncated */
  truncateText: (text: string, maxLength: number) => string

  // Status formatting
  /** Format contract status for display using i18n */
  getContractStatusLabel: (status: ContractStatus | undefined) => string

  // Legacy functions for backwards compatibility with useFormatting
  /** @deprecated Use formatDateShort instead */
  formatDate: (timestamp: number) => string
  /** Get full name with Dutch prefix (tussenvoegsel) */
  getFullName: (user: UserLike) => string
  /** Get initials from user name */
  getInitials: (user: UserLike) => string
}

/**
 * Composable for locale-aware formatting.
 *
 * Uses the current vue-i18n locale to format dates, times, and numbers
 * according to the user's language preferences.
 *
 * @example
 * ```vue
 * <script setup lang="ts">
 * import { useFormatters } from '@/composables/useFormatters'
 *
 * const { formatDateShort, formatCurrency, formatRelativeTime } = useFormatters()
 *
 * const date = new Date()
 * const amount = 1234.56
 * </script>
 *
 * <template>
 *   <p>{{ formatDateShort(date) }}</p>
 *   <p>{{ formatCurrency(amount) }}</p>
 *   <p>{{ formatRelativeTime(date) }}</p>
 * </template>
 * ```
 */
export function useFormatters(): UseFormattersReturn {
  const { locale, t } = useI18n()

  const intlLocale = computed(() => toIntlLocale(locale.value))

  // Duration formatting
  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    if (hours === 0) return t('appointments.duration.minutes', { count: mins })
    if (mins === 0) return t('appointments.duration.hours', { count: hours })
    return t('appointments.duration.hoursAndMinutes', { hours, minutes: mins })
  }

  // Date formatting functions
  const formatDateShort = (date: DateInput) =>
    formatDateShortUtil(date, intlLocale.value)

  const formatDateLong = (date: DateInput) =>
    formatDateLongUtil(date, intlLocale.value)

  const formatTime = (date: DateInput) => formatTimeUtil(date, intlLocale.value)

  const formatDateTime = (date: DateInput) =>
    formatDateTimeUtil(date, intlLocale.value)

  const formatRelativeTime = (date: DateInput) =>
    formatRelativeTimeUtil(date, intlLocale.value)

  const formatAppointmentDateTime = (date: DateInput) =>
    formatAppointmentDateTimeUtil(date, intlLocale.value)

  const formatDateCustom = (date: DateInput, options: Intl.DateTimeFormatOptions) =>
    formatDateCustomUtil(date, options, intlLocale.value)

  const formatTimeRange = (startDate: DateInput, endDate: DateInput) =>
    formatTimeRangeUtil(startDate, endDate, intlLocale.value)

  const formatDateWithWeekday = (date: DateInput) =>
    formatDateWithWeekdayUtil(date, intlLocale.value)

  const formatDateShortWithWeekday = (date: DateInput) =>
    formatDateShortWithWeekdayUtil(date, intlLocale.value)

  const formatDateDigits = (date: DateInput) => formatDateDigitsUtil(date, intlLocale.value)

  // Number formatting functions
  const formatNumber = (value: number | undefined | null, options?: Intl.NumberFormatOptions) =>
    formatNumberUtil(value, intlLocale.value, options)

  const formatCurrency = (amount: number | undefined | null, currency = 'EUR') =>
    formatCurrencyUtil(amount, intlLocale.value, currency)

  const formatCurrencyOrDash = (amount: number | undefined | null, currency = 'EUR') =>
    amount == null ? '—' : formatCurrency(amount, currency)

  const formatPercent = (
    value: number | undefined | null,
    minimumFractionDigits = 0,
    maximumFractionDigits = 1
  ) => formatPercentUtil(value, intlLocale.value, minimumFractionDigits, maximumFractionDigits)

  const formatFileSize = (bytes: number | undefined | null) =>
    formatFileSizeUtil(bytes, intlLocale.value)

  // Boolean formatting
  const formatBoolean = (value: boolean | null | undefined): string => {
    if (value === null || value === undefined) return '-'
    return value ? t('common.yes') : t('common.no')
  }

  // Status formatting
  const getContractStatusLabel = (status: ContractStatus | undefined): string => {
    if (!status) {
      return t('contracts.status.unknown')
    }
    return t(`contracts.status.${status.toLowerCase()}`)
  }

  // Legacy/utility functions for backwards compatibility
  const formatDate = (timestamp: number) => formatDateShort(timestamp)

  const getFullName = (user: UserLike): string => getFullNameUtil(user)

  const getInitials = (user: UserLike): string => getInitialsUtil(user)

  return {
    intlLocale,
    formatDuration,
    formatDateShort,
    formatDateLong,
    formatTime,
    formatDateTime,
    formatRelativeTime,
    formatAppointmentDateTime,
    formatDateCustom,
    formatTimeRange,
    formatDateWithWeekday,
    formatDateShortWithWeekday,
    formatDateDigits,
    formatDateNumeric,
    formatNumber,
    formatCurrency,
    formatCurrencyOrDash,
    formatPercent,
    formatFileSize,
    formatBoolean,
    truncateText,
    getContractStatusLabel,
    formatDate,
    getFullName,
    getInitials,
  }
}
